import { motion } from "framer-motion";
import { Car, MapPin, Banknote, Ruler, Trophy, CalendarClock } from "lucide-react";
import { RAFFLE } from "@/lib/raffleData";
import { useSiteImages, useSiteVideos } from "@/lib/siteImages";

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Prizes() {
  const images = useSiteImages();
  const videos = useSiteVideos();
  const videoList = [videos.video1, videos.video2].filter(Boolean);
  return (
    <section
      id="premios"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="prizes-section"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#051A0F] via-[#0A2818] to-[#051A0F]" />
      <div className="absolute top-1/4 -right-40 w-96 h-96 bg-[#F0B91B]/10 blur-3xl rounded-full" />
      <div className="absolute bottom-1/4 -left-40 w-96 h-96 bg-[#7FD1AE]/10 blur-3xl rounded-full" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        {/* Promo videos (vertical 9:16) above the title */}
        {videoList.length > 0 && (
          <motion.div
            {...fadeIn(0)}
            className="mb-14 flex flex-wrap justify-center gap-5 md:gap-8"
            data-testid="promo-videos"
          >
            {videoList.map((src, i) => (
              <div
                key={i}
                className="relative w-[220px] md:w-[300px] aspect-[9/16] rounded-3xl overflow-hidden border-2 border-[#F0B91B]/40 bg-black shadow-[0_20px_60px_-20px_rgba(240,185,27,0.5)]"
                data-testid={`promo-video-${i + 1}`}
              >
                <video
                  src={src}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  preload="metadata"
                />
              </div>
            ))}
          </motion.div>
        )}

        {/* BETMAR por Colombia banner */}
        <motion.a
          {...fadeIn(0)}
          href="#elegir-ficha"
          className="block relative mb-14 md:mb-20 rounded-3xl overflow-hidden border-2 border-[#F0B91B]/40 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.85)] hover:border-[#F0B91B]/70 hover:-translate-y-1 transition-all duration-500 group"
          data-testid="betmar-colombia-banner"
          aria-label="BETMAR por Colombia — Reconstrucción · Donaremos el 20% de este evento"
        >
          <img
            src="https://customer-assets-jai6qajn.emergentagent.net/job_landing-preview-144/artifacts/7u934rfb_banner%20ayuda%2020.webp"
            alt="BETMAR por Colombia — Donaremos el 20% de este evento a la reconstrucción de Colombia tras el temblor. Spark Life 2015 · Lote 208 m² en Acacías"
            className="w-full h-auto object-cover group-hover:scale-[1.01] transition-transform duration-700"
            loading="lazy"
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-[#F0B91B]/25 rounded-3xl pointer-events-none" />
        </motion.a>

        {/* Section header */}
        <motion.div {...fadeIn(0)} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F0B91B]/30 bg-[#F0B91B]/5 mb-6">
            <Trophy className="w-4 h-4 text-[#F0B91B]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#F0B91B] font-semibold font-outfit">
              Las Entregas
            </span>
          </div>
          <h2 className="font-bebas text-5xl md:text-7xl text-[#FDFBF7] tracking-wider">
            Dos entregas. <span className="gold-shimmer">Una sola ficha.</span>
          </h2>
          <p className="mt-4 text-lg text-[#FDFBF7]/70 max-w-2xl mx-auto">
            El ganador del Gran Proyecto {RAFFLE.brand} se lleva un lote de 208 m² en{" "}
            {RAFFLE.location} <strong className="text-[#F0B91B]">y</strong> un Chevrolet Spark Life 2015.
          </p>
        </motion.div>

        {/* Bento grid: Car (large) + Lote */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {/* Car */}
          <motion.article
            {...fadeIn(0.1)}
            className="md:col-span-7 group relative overflow-hidden rounded-3xl border border-[#F0B91B]/25 bg-[#0A2818] shadow-[0_10px_40px_-15px_rgba(0,0,0,0.7)] hover:border-[#F0B91B]/60 hover:-translate-y-1 transition-all duration-500"
            data-testid="prize-car"
          >
            <div className="absolute inset-0">
              <img
                src={images.car}
                alt="Chevrolet Spark Life 2015 rojo"
                className="w-full h-full object-cover opacity-80 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#051A0F] via-[#051A0F]/60 to-transparent" />
            </div>
            <div className="relative p-8 md:p-12 min-h-[440px] md:min-h-[560px] flex flex-col justify-end">
              <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-[#F0B91B] text-[#051A0F] font-bebas tracking-wider text-sm mb-4">
                <Car className="w-4 h-4" />
                Entrega 01
              </div>
              <h3 className="font-bebas text-5xl md:text-7xl text-[#FDFBF7] leading-none tracking-wider">
                Chevrolet <span className="text-[#F0B91B]">Spark Life</span> 2015
              </h3>
              <p className="mt-4 text-[#FDFBF7]/85 text-base md:text-lg max-w-xl">
                Compacto, ágil y perfecto para la ciudad. Un carro rojo con papeles al día,
                listo para rodar el mismo día del sorteo.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                {["Modelo 2015", "Rojo pasión", "Papeles al día", "Único ganador"].map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border border-[#F0B91B]/40 text-[#FDFBF7]/90 backdrop-blur-sm bg-[#051A0F]/40 font-outfit"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </motion.article>

          {/* Lote */}
          <motion.article
            {...fadeIn(0.2)}
            className="md:col-span-5 group relative overflow-hidden rounded-3xl border border-[#F0B91B]/25 bg-[#0A2818] hover:border-[#F0B91B]/60 hover:-translate-y-1 transition-all duration-500"
            data-testid="prize-lote"
          >
            <div className="absolute inset-0">
              <img
                src={images.lote}
                alt="Lote de 208 m² en Acacías, Meta"
                className="w-full h-full object-cover opacity-70 group-hover:opacity-85 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#051A0F] via-[#051A0F]/70 to-transparent" />
            </div>
            <div className="relative p-6 md:p-8 min-h-[440px] md:min-h-[560px] flex flex-col justify-end">
              <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-[#7FD1AE] text-[#051A0F] font-bebas tracking-wider text-xs mb-3">
                <MapPin className="w-3.5 h-3.5" />
                Entrega 02
              </div>
              <h3 className="font-bebas text-4xl md:text-6xl text-[#FDFBF7] leading-none tracking-wider">
                Lote <span className="text-[#F0B91B]">208 m²</span>
              </h3>
              <p className="mt-2 text-sm md:text-base text-[#FDFBF7]/80">{RAFFLE.location}</p>
              <div className="mt-5 grid grid-cols-2 gap-2 text-center">
                <div className="rounded-xl bg-[#051A0F]/60 border border-[#F0B91B]/20 py-3">
                  <div className="font-bebas text-2xl md:text-3xl text-[#F0B91B]">{RAFFLE.lote.frente}m</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#FDFBF7]/60">Frente</div>
                </div>
                <div className="rounded-xl bg-[#051A0F]/60 border border-[#F0B91B]/20 py-3">
                  <div className="font-bebas text-2xl md:text-3xl text-[#F0B91B]">{RAFFLE.lote.fondo}m</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#FDFBF7]/60">Fondo</div>
                </div>
              </div>
            </div>
          </motion.article>
        </div>

        {/* Entrega anticipada band ($10.000.000) */}
        <motion.div
          {...fadeIn(0.3)}
          className="mt-6 relative overflow-hidden rounded-3xl border border-[#E63946]/30 bg-gradient-to-r from-[#0A2818] via-[#0A2818] to-[#051A0F]"
          data-testid="early-prize"
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "url(https://images.unsplash.com/photo-1593672715438-d88a70629abe)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#051A0F] via-[#051A0F]/85 to-[#051A0F]/40" />
          <div className="relative p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E63946] text-[#FDFBF7] font-bebas tracking-wider text-xs mb-3">
                <Banknote className="w-3.5 h-3.5" />
                Entrega Anticipada
              </div>
              <h3 className="font-bebas text-5xl md:text-7xl gold-shimmer leading-none tracking-wider">
                ${RAFFLE.earlyPrize}
              </h3>
              <p className="mt-2 text-sm md:text-base text-[#FDFBF7]/80 font-outfit max-w-xl">
                Diez millones de pesos en efectivo, entregados de forma anticipada.
              </p>
            </div>
            <div className="flex items-center gap-3 self-start md:self-center">
              <div className="rounded-2xl border border-[#F0B91B]/30 bg-[#051A0F]/70 backdrop-blur-md px-6 py-4 text-center">
                <div className="flex items-center gap-2 justify-center text-[#F0B91B] mb-1">
                  <CalendarClock className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold">
                    Fecha de entrega
                  </span>
                </div>
                <div className="font-bebas text-3xl md:text-4xl text-[#FDFBF7] tracking-wider">
                  {RAFFLE.earlyDate}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#F0B91B]/80 font-outfit mt-1">
                  {RAFFLE.earlyDateNote}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Info row */}
        <motion.div
          {...fadeIn(0.4)}
          className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {[
            {
              icon: Ruler,
              title: "Documentación al día",
              text: "Todas las entregas incluyen traspaso legal y documentación completa.",
            },
            {
              icon: Trophy,
              title: "Sorteo transparente",
              text: "Con Lotería de la ciudad. Cada ficha tiene un número único.",
            },
            {
              icon: MapPin,
              title: "Entrega inmediata",
              text: "Las entregas se realizan el mismo día del sorteo al ganador.",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="rounded-2xl border border-[#F0B91B]/15 bg-[#0A2818]/60 backdrop-blur-md p-5 flex gap-4 items-start"
            >
              <div className="w-10 h-10 rounded-full bg-[#F0B91B]/15 flex items-center justify-center flex-shrink-0">
                <f.icon className="w-5 h-5 text-[#F0B91B]" />
              </div>
              <div>
                <div className="font-bebas text-xl text-[#FDFBF7] tracking-wide">
                  {f.title}
                </div>
                <p className="text-sm text-[#FDFBF7]/70 mt-1 font-outfit">{f.text}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Inline CTA */}
        <motion.div {...fadeIn(0.5)} className="mt-12 text-center">
          <a
            href="#elegir-ficha"
            className="inline-flex items-center gap-3 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] px-8 py-4 rounded-full font-bebas text-2xl tracking-wider shadow-[0_15px_40px_-10px_rgba(240,185,27,0.6)] hover:-translate-y-1 transition-all"
            data-testid="prizes-cta"
          >
            Quiero mi Ficha
          </a>
        </motion.div>
      </div>
    </section>
  );
}
